import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { useRootStore } from '../../../hooks/useRootStore';
import { getBaseUrlForBrand } from '../../../lib/brand';
import Copyright from '../../Copyright';
import { KeyIcon } from '../../Icons/KeyIcon';
import SignInMessage from './SignInMessage';

export type HomeContentProps = {
    content: string;
    contentTitle: string;
    heroLabel: string;
    intro: string;
    loginButtonLabel: string;
};

const HomeContent: React.FC<React.PropsWithChildren<HomeContentProps>> = ({ content, contentTitle, heroLabel, intro, loginButtonLabel }) => {
    const store = useRootStore();
    const loginLink = `/login?${new URLSearchParams({
        returnTo: new URL('/self-service/manage-bookings', getBaseUrlForBrand(store.brand)).href,
    })}`;

    return (
        <Box sx={{ color: '#636466' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    width: '100%',
                    height: {
                        xs: '100%',
                        sm: 'auto',
                    },
                }}
            >
                <Box
                    sx={(theme) => ({
                        '& >span': { marginTop: theme.spacing(1), marginLeft: theme.spacing(2) },
                        backgroundColor: theme.palette.secondary.main,
                        width: '100%',
                        fontFamily: store.brand === 'jucy' ? theme.typography.alternate.fontFamily : undefined,
                        fontWeight: store.brand === 'jucy' ? undefined : 'bold',
                        color: theme.palette.secondary.contrastText,
                        display: 'flex',
                        alignItems: 'center',
                        padding: theme.spacing(2),
                        fontSize: 42,
                        lineHeight: 1,
                    })}
                >
                    <KeyIcon color={store.brand === 'jucy' ? 'secondary' : 'primary'} sx={{ width: 55, height: 55 }} />
                    <span>{heroLabel}</span>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 2,
                        pr: 1.5,
                        pl: 1.5,
                        pb: 2,
                    }}
                >
                    <Box
                        sx={{
                            textAlign: 'center',
                            width: '100%',
                            py: 4,
                            px: 2,
                        }}
                    >
                        <Typography align="center" component="h2" variant="subtitle1" dangerouslySetInnerHTML={{ __html: intro }} />
                    </Box>
                    <Button
                        component={Link}
                        href={loginLink}
                        prefetch={false}
                        sx={{ borderRadius: 0, boxShadow: 'none', fontSize: 22, paddingX: '1.25rem' }}
                        color="primary"
                        variant="contained"
                    >
                        {loginButtonLabel}
                    </Button>
                    <SignInMessage />
                    <Box sx={{ mt: 6, width: '100%' }}>
                        {contentTitle.length ? (
                            <Typography align="left" component="h2" variant="subtitle1" sx={{ px: '1rem', fontWeight: 'bold' }}>
                                {contentTitle}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            py: 0,
                            px: 1,
                            width: '100%',
                        }}
                    >
                        <Typography align="left" component="div" variant="body2" dangerouslySetInnerHTML={{ __html: content }} />
                    </Box>
                </Box>
                <Box
                    sx={{
                        marginTop: 'auto',
                        fontSize: 12,
                    }}
                >
                    <Copyright sx={{ pb: 2 }} />
                </Box>
            </Box>
        </Box>
    );
};

export default HomeContent;
